import { ActionIcon, Button, Flex, Menu, Modal, Radio, Stack, Text, Tooltip } from '@mantine/core';
import Card from 'components/Card';
import DataTables from 'components/DataTables';
import Toolbar from 'components/Toolbar';
import React, { useState, useEffect } from 'react';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconDots,
  IconEye
} from '@tabler/icons-react';
import { CompetencyData } from 'types/dacum/competencyManagement';
import { useDisclosure } from '@mantine/hooks';
import FilterCompetencyManagement from './FilterCompetencyManagement';
import { useCompetencyManagementStore } from 'store/competencyManagement';
import { useNavigate, useLocation } from 'react-router-dom';
import { pathnames } from 'routes/dacum';
import { useQuery } from '@tanstack/react-query';
import { reviewMasterPlanService } from 'services/dacum/reviewMasterPlan.service';

export const staticCompetencyData = [
  {
    competencytype: 'Core Competency',
    competencyName: 'Core Competency',
    competencyDescription:
      'Core competencies are fundamental skills and behaviors required for all employees.',
    businessunitid: 'All',
    businessunitname: 'All',
    jobfamilyid: 'All',
    jobfamilyname: 'All',
    jobFamilyGroupName: 'Core',
    schoolName: 'School of Core Competency',
    schoolID: 'SC-CORE-001',
    msSchoolCompetencyManagementID: 'E5691765-D7D6-44D9-BA67-530304A70444',
    jobband: {
      data: ['Supervisor', 'Staff', 'Manager', 'Officer']
    }
  },
  {
    competencytype: 'Leader Competency',
    competencyName: 'Leader Competency',
    competencyDescription:
      'Leader competencies are skills and behaviors required for leadership roles.',
    businessunitid: 'All',
    businessunitname: 'All',
    jobfamilyid: 'All',
    jobfamilyname: 'All',
    jobFamilyGroupName: 'Leader',
    schoolName: 'School of Leadership',
    schoolID: 'SC-LEAD-001',
    msSchoolCompetencyManagementID: '5EC0408B-35E6-4A7D-B382-4A52A645A4BF',
    jobband: {
      data: ['Supervisor', 'Staff', 'Manager', 'Officer']
    }
  },
  {
    competencytype: 'FM Competency',
    competencyName: 'FM Competency',
    competencyDescription: 'FM competencies are specialized skills for Facility Management roles.',
    businessunitid: 'All',
    businessunitname: 'All',
    jobfamilyid: 'All',
    jobfamilyname: 'All',
    jobFamilyGroupName: 'FM',
    schoolName: 'School of FM Competency',
    schoolID: 'SC-FM-001',
    msSchoolCompetencyManagementID: '0E45B122-0160-42A1-828D-2DC24EC60B90',
    jobband: {
      data: ['Junior FM', 'FM', 'Senior FM', 'Associate Professor', 'Professor']
    }
  }
];

const TableCompetencyManagement = () => {
  const [filterOpened, { open: openFilterModal, close: closeFilterModal }] = useDisclosure();
  const [jobBandModalOpened, { open: openJobBandModal, close: closeJobBandModal }] =
    useDisclosure(false);
  const [
    schoolWarningModalOpened,
    { open: openSchoolWarningModal, close: closeSchoolWarningModal }
  ] = useDisclosure(false);
  const { setMode, setFormData } = useCompetencyManagementStore();
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';

  // local filter state to store selected jobFamily / division
  const [filters, setFilters] = React.useState<{
    jobfamilyid?: string;
    division?: string;
  }>({});
  const [pendingRow, setPendingRow] = useState<any | null>(null);
  const [selectedJobBand, setSelectedJobBand] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Reset to page 1 whenever the search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const { data: competencyData, isPending } = useQuery({
    queryKey: ['competencyManagement', searchQuery, filters, currentPage, pageSize],
    queryFn: () =>
      reviewMasterPlanService.getReviewMasterPlan({
        pageNumber: currentPage,
        pageSize: pageSize,
        search: searchQuery,
        jobfamilyid: filters.jobfamilyid,
        division: filters.division
      })
  });

  const resolveJobBands = React.useCallback((row: any) => {
    if (!row) return [] as string[];

    // Check if jobband is an object with data property
    const bandFromData = row.jobband?.data;
    if (Array.isArray(bandFromData) && bandFromData.length > 0) {
      return Array.from(new Set(bandFromData.filter((band: string) => Boolean(band))));
    }

    // Check if jobband itself is an array
    const rawValue = row.jobBand ?? row.jobband;
    if (Array.isArray(rawValue)) {
      return Array.from(new Set(rawValue.filter((band: string) => Boolean(band))));
    }

    // Check if jobband is an object (handle case where jobband is the object itself)
    if (typeof rawValue === 'object' && rawValue !== null && 'data' in rawValue) {
      const dataArray = rawValue.data;
      if (Array.isArray(dataArray)) {
        return Array.from(new Set(dataArray.filter((band: string) => Boolean(band))));
      }
    }

    // Fallback for string value
    if (typeof rawValue === 'string' && rawValue) {
      return [rawValue];
    }

    return [] as string[];
  }, []);

  const navigateToDetail = React.useCallback(
    (row: any, managementLevel?: string) => {
      setMode('view');
      setFormData(row as Partial<CompetencyData>);

      const businessUnitId = row.businessunitid as string;
      const jobFamilyId = row.jobfamilyid || (row.jobFamilyID as string);
      const jobFamilyName = row.jobfamilyname as string;
      const businessUnitName = row.businessunitname as string;
      const competencyType = row.competencytype as string;
      const jobFamilyGroupName = row.jobFamilyGroupName as string;
      const msSchoolCompetencyManagementID =
        row.msSchoolCompetencyManagementID || row.schoolID || row.schoolId || row.schoolid;

      const params = new URLSearchParams();
      if (jobFamilyId) params.set('jobfamilyid', jobFamilyId);
      if (managementLevel) params.set('managementlevel', managementLevel);
      if (businessUnitId) params.set('businessunitid', businessUnitId);
      if (jobFamilyName) params.set('jobfamilyname', jobFamilyName);
      if (businessUnitName) params.set('businessunitname', businessUnitName);
      if (competencyType) params.set('competencytype', competencyType);
      if (jobFamilyGroupName) params.set('jobfamilygroupname', jobFamilyGroupName);
      if (msSchoolCompetencyManagementID)
        params.set('msSchoolCompetencyManagementID', msSchoolCompetencyManagementID);

      navigate({
        pathname: pathnames.detailCompetencyManagement,
        search: params.toString()
      });
    },
    [navigate, setFormData, setMode]
  );

  const proceedToView = (row: any) => {
    const jobBandOptions = resolveJobBands(row);

    if (jobBandOptions.length > 1) {
      setPendingRow(row);
      setSelectedJobBand(jobBandOptions[0] || '');
      openJobBandModal();
      return;
    }

    navigateToDetail(row, jobBandOptions[0]);
  };

  const handleView = (row: any) => {
    const hasSchool =
      row.msSchoolCompetencyManagementID ||
      row.schoolID ||
      row.schoolId ||
      row.schoolid ||
      row.schoolName;

    if (!hasSchool) {
      setPendingRow(row);
      openSchoolWarningModal();
      return;
    }

    proceedToView(row);
  };

  const handleConfirmSchoolWarning = () => {
    const row = pendingRow;
    closeSchoolWarningModal();
    if (row) proceedToView(row);
  };

  const handleConfirmJobBand = () => {
    if (!pendingRow || !selectedJobBand) return;
    navigateToDetail(pendingRow, selectedJobBand);
    handleCloseJobBandModal();
  };

  const handleCloseJobBandModal = () => {
    closeJobBandModal();
    setPendingRow(null);
    setSelectedJobBand('');
  };

  const defaultColumns = [
    // {
    //   heading: 'Competency Name',
    //   props: (row: any) => row.competencyName || row.competencyname || '-',
    //   sortable: true,
    //   active: true,
    //   id: 'competencyName'
    // },
    {
      heading: 'Competency Type',
      props: 'competencytype',
      sortable: true,
      active: true,
      id: 'competencytype'
    },
    {
      heading: 'Business Unit Name',
      props: 'businessunitname',
      sortable: true,
      active: true,
      id: 'businessunitname'
    },
    {
      heading: 'Job Family Name',
      props: 'jobfamilyname',
      sortable: true,
      active: true,
      id: 'jobfamilyname'
    },
    {
      heading: 'Job Family Group Name',
      props: 'jobFamilyGroupName',
      sortable: true,
      active: true,
      id: 'jobFamilyGroupName'
    },
    {
      heading: 'School Name',
      props: (row: any) => {
        const schoolId =
          row.schoolID || row.schoolId || row.schoolid || row.msSchoolCompetencyManagementID;
        const schoolName = row.schoolName;

        if (!schoolName && !schoolId) {
          return (
            <Tooltip
              label='No school competency mapped. Required for master plan submission.'
              withinPortal
            >
              <Flex align='center' gap={6} style={{ cursor: 'default' }}>
                <IconAlertTriangle size={16} color='var(--mantine-color-orange-6)' />
                <Text fz={14} c='orange.6' fw={500}>
                  Not Mapped
                </Text>
              </Flex>
            </Tooltip>
          );
        }

        const content = (
          <Flex align='center' gap={8} wrap='wrap'>
            <Text fz={14} fw={500}>
              {schoolName || '-'}
            </Text>
          </Flex>
        );

        if (!schoolId) return content;

        return (
          <Tooltip label={`School ID: ${schoolId}`} withinPortal>
            {content}
          </Tooltip>
        );
      },
      sortable: true,
      active: true,
      id: 'schoolName'
    },
    {
      heading: 'Year',
      props: (row: any) => row.tahun?.toString() || '-',
      sortable: true,
      active: true,
      id: 'tahun'
    },
    {
      heading: 'Job Band',
      props: (row: any) => row.jobband?.data?.join(', ') || '',
      sortable: true,
      active: true,
      id: 'jobband'
    },
    {
      heading: 'Status',
      props: (row: any) => {
        if (row.status === undefined || row.status === null) {
          return (
            <Tooltip label='Not monitored' withinPortal>
              <ActionIcon variant='transparent' c='dimmed'>
                <IconDots size={20} />
              </ActionIcon>
            </Tooltip>
          );
        }
        const isCreated = row.status === true;
        return (
          <Tooltip label={isCreated ? 'Created on DACUM Competency' : 'Not Created'} withinPortal>
            <ActionIcon variant='transparent' c={isCreated ? 'green' : 'gray'}>
              {isCreated ? <IconCircleCheck size={20} /> : <IconCircleX size={20} />}
            </ActionIcon>
          </Tooltip>
        );
      },
      sortable: false,
      active: true,
      id: 'status'
    },
    {
      heading: 'Action',
      props: (row: any) => (
        <Menu shadow='md'>
          <Flex gap={4}>
            <Tooltip label={'View Detail'}>
              <ActionIcon variant='transparent' c={'grey.5'} onClick={() => handleView(row)}>
                <IconEye />
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Menu>
      ),
      active: true
    }
  ];

  const [columns, setColumns] = useState(defaultColumns as any[]);

  const jobBandOptions = React.useMemo(
    () => resolveJobBands(pendingRow),
    [pendingRow, resolveJobBands]
  );

  return (
    <>
      <Card>
        <Stack>
          <Toolbar
            columns={columns}
            onFilter={openFilterModal}
            showFilterColumn
            setColumns={setColumns}
            showCreateButton={false}
          />

          <DataTables
            columns={columns}
            data={[...staticCompetencyData, ...(competencyData?.data || [])]}
            loading={isPending}
            withPagination={true}
            totalData={(competencyData?.totalRecords || 0) + staticCompetencyData.length}
            currentPage={currentPage}
            currentSize={pageSize}
            handleChangePage={(page) => setCurrentPage(page)}
            handleChangeSize={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </Stack>
      </Card>

      <FilterCompetencyManagement
        opened={filterOpened}
        onClose={closeFilterModal}
        onSubmitForm={(data) => {
          setFilters({
            jobfamilyid: data.jobfamilyid || undefined,
            division: data.division || undefined
          });
          setCurrentPage(1);
        }}
      />

      <Modal
        opened={jobBandModalOpened}
        onClose={handleCloseJobBandModal}
        title='Select Job Band'
        centered
      >
        <Stack gap='md'>
          <Text size='sm' c='dimmed'>
            Choose a Job Band to view curriculum mapping detail.
          </Text>
          <Radio.Group value={selectedJobBand} onChange={setSelectedJobBand}>
            <Stack gap='xs'>
              {jobBandOptions.map((band) => (
                <Radio key={band} value={band} label={band} />
              ))}
            </Stack>
          </Radio.Group>
          <Flex justify='flex-end' gap='sm'>
            <Button variant='default' onClick={handleCloseJobBandModal}>
              Cancel
            </Button>
            <Button onClick={handleConfirmJobBand} disabled={!selectedJobBand}>
              Continue
            </Button>
          </Flex>
        </Stack>
      </Modal>

      <Modal
        opened={schoolWarningModalOpened}
        onClose={closeSchoolWarningModal}
        title='School Competency Not Mapped'
        centered
      >
        <Stack gap='md'>
          <Flex gap='sm' align='flex-start'>
            <IconAlertTriangle
              size={24}
              color='var(--mantine-color-orange-6)'
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <Stack gap='xs'>
              <Text size='sm'>This competency does not have a school competency mapped.</Text>
              <Text size='sm' c='dimmed'>
                School competency mapping is <strong>required</strong> for master plan submission.
                You can still view the detail, but this competency cannot be included in a master
                plan until a school is mapped.
              </Text>
            </Stack>
          </Flex>
          <Flex justify='flex-end' gap='sm'>
            <Button variant='default' onClick={closeSchoolWarningModal}>
              Cancel
            </Button>
            <Button color='orange' onClick={handleConfirmSchoolWarning}>
              Continue Anyway
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};

export default TableCompetencyManagement;
